import React, { useEffect, useMemo, useState } from "react";

// mui components
import {
	Box,
	Checkbox,
	IconButton,
	Stack,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import { DownloadOutlined, VisibilityOutlined } from "@mui/icons-material";

// custom components
import Body2 from "../../typography/body-02";
import Body3 from "../../typography/body-03";
import { useGetUserEmailByDocumentMutation } from "../../../features/get-user-email-by-document/getUserEmailByDocumentApiSlice";
import { isBrowserRenderable } from "../../../utils/document-validation";
import { getDocumentPipelineStatusPresentation } from "../../../utils/document-pipeline-status";
import {
	documentTrackerUploadedByCache,
	documentTrackerUploadedByRequestCache,
	getDocumentTrackerUploadedByDocumentId,
	getDocumentTrackerUploadedByEmailFromResponse,
	getDocumentTrackerUploadedByFallbackText,
	shouldResolveDocumentTrackerUploadedBy,
} from "../../../utils/document-tracker-uploaded-by";

// custom styles
import styles from "../index.module.scss";
import { DateTime } from "luxon";

// Scoped to this row's "View" action only - Encompass accepts these
// extensions for upload, but browsers won't render them inline, so the
// View click should offer a download instead. Intentionally NOT folded
// into the shared isBrowserRenderable() (document-validation.js), since
// that helper is also used by card/loan-documents and
// table-row/loan-documents, whose behavior must stay unchanged.
const VIEW_ACTION_NON_RENDERABLE_EXTENSIONS = new Set([
	"doc",
	"docx",
	"tif",
	"emf",
	"xps",
]);

const isViewableInBrowser = (fileName) => {
	if (!isBrowserRenderable(fileName)) return false;
	const extension = String(fileName ?? "")
		.toLowerCase()
		.split(".")
		.pop();
	return !VIEW_ACTION_NON_RENDERABLE_EXTENSIONS.has(extension);
};

const normalizeValue = (value) => String(value ?? "").trim();

const getRecordIdentifier = (record) =>
	normalizeValue(
		record?.id ??
			record?.entityId ??
			record?.documentId ??
			record?.attachmentId ??
			record?.entityID ??
			record?.document_id ??
			record?.name ??
			record?.title ??
			record
	);

const getConditionEntries = (conditions) => {
	if (Array.isArray(conditions)) {
		return conditions.filter(Boolean);
	}

	if (Array.isArray(conditions?.items)) {
		return conditions.items.filter(Boolean);
	}

	if (Array.isArray(conditions?.results)) {
		return conditions.results.filter(Boolean);
	}

	if (Array.isArray(conditions?.data)) {
		return conditions.data.filter(Boolean);
	}

	if (!conditions) {
		return [];
	}

	return [conditions];
};

const getRecordKey = (record, index) =>
	normalizeValue(
		record?.id ??
			record?.entityId ??
			record?.documentId ??
			record?.attachmentId ??
			record?.entityID ??
			record?.name ??
			record?.title ??
			[
				...(Array.isArray(record?.attachments) ? record.attachments : []),
				...(Array.isArray(record?.conditions) ? record.conditions : []),
			]
				.map(getRecordIdentifier)
				.filter(Boolean)
				.join("|")
	) || `condition-document-${index}`;

// Mirrors the "Uploaded By" cell's own display logic (see createdByDisplayText
// below) so sorting that column matches what's actually shown, instead of
// sorting on row.createdBy.entityId - which is the same shared service
// account ("appcbportal_test") on every portal-uploaded row, so sorting by it
// does nothing. documentEmailCache is populated asynchronously per row (see
// the useEffect below); reading it here means sorting reflects whatever has
// resolved by the time the user clicks the column header.
export const getUploadedByDisplayText = (row) => {
	if (row?.__isPendingRetryRecord) {
		return row?.uploadedBy ?? "";
	}

	const documentId = getDocumentTrackerUploadedByDocumentId(row);
	const uploadedByFallbackText = getDocumentTrackerUploadedByFallbackText(row);

	if (!shouldResolveDocumentTrackerUploadedBy(row)) {
		return uploadedByFallbackText;
	}

	const cachedEmail = documentId
		? documentTrackerUploadedByCache.get(documentId)
		: undefined;
	return cachedEmail || uploadedByFallbackText;
};

export const getTrackerDocumentSelectionId = (document) =>
	normalizeValue(
		document?.entityId ??
			document?.id ??
			document?.attachmentId ??
			document?.documentId ??
			document?.uploadKey ??
			document?.__stableUploadKey ??
			document?.entityID ??
			""
	);

export const getTrackerMatchingConditionInfo = (
	row,
	conditionDocuments = []
) => {
	const documentId = normalizeValue(
		row?.document_id ?? row?.documentId ?? row?.entityId ?? row?.id ?? ""
	);

	if (!documentId) {
		return {
			matchingConditionDocuments: [],
			matchingConditionCount: 0,
			isIndexed: false,
			isXpanseProcessingRecord: false,
		};
	}

	const matchedRecords = [];
	const seenRecordKeys = new Set();
	let hasMatchingAttachment = false;
	let isIndexed = false;
	let indexedRecordTitle = "";
	let isXpanseProcessingRecord = false;

	(conditionDocuments || []).forEach((doc, index) => {
		const attachments = Array.isArray(doc?.attachments) ? doc.attachments : [];
		const conditions = getConditionEntries(doc?.conditions);
		const hasConditionData = conditions.length > 0;
		const hasConditionsKey =
			doc?.conditions !== undefined && doc?.conditions !== null;
		const normalizedTitle = normalizeValue(doc?.title).toLowerCase();
		const recordHasMatchingAttachment = attachments.some(
			(attachment) => getRecordIdentifier(attachment) === documentId
		);

		if (recordHasMatchingAttachment) {
			hasMatchingAttachment = true;
		}

		if (
			recordHasMatchingAttachment &&
			!hasConditionsKey &&
			normalizedTitle !== "xpanse processing"
		) {
			isIndexed = true;
			if (!indexedRecordTitle) {
				indexedRecordTitle = normalizeValue(doc?.title);
			}
		}

		if (
			recordHasMatchingAttachment &&
			normalizedTitle === "xpanse processing"
		) {
			isXpanseProcessingRecord = true;
		}

		if (!recordHasMatchingAttachment || !hasConditionData) {
			return;
		}

		const recordKey = getRecordKey(doc, index);

		if (recordKey && seenRecordKeys.has(recordKey)) {
			return;
		}

		if (recordKey) {
			seenRecordKeys.add(recordKey);
		}

		matchedRecords.push({
			...doc,
			conditionCount: conditions.length,
		});
	});

	return {
		matchingConditionDocuments: matchedRecords,
		matchingConditionCount: matchedRecords.reduce(
			(total, document) => total + (document?.conditionCount ?? 0),
			0
		),
		hasMatchingAttachment,
		isIndexed,
		indexedRecordTitle,
		isXpanseProcessingRecord,
	};
};

const ConditionsDocumentTrackerRow = ({
	row,
	selectedRow = [],
	selectedDocumentIds = [],
	onToggleDocumentSelection = () => {},
	activeRowId = null,
	setActiveRowId = () => {},
	toggleDrawer = () => {},
	loanId,
	conditionTypeOptions = [],
	setConditionsOptions = () => {},
	isConditionStatusLoaded = false,
	selectedConditionTypes = {},
	setSelectedConditionTypes = () => {},
	selectedDocuments = [],
	conditionDocuments = [],
	onRefreshDocumentsList = () => {},
	minimizedRow,
	handleAddMinimizeRow,
	handleDeleteMinimizeRow,
	onViewDocument = () => {},
	onDownloadDocument = () => {},
}) => {
	const [getUserEmailByDocument] = useGetUserEmailByDocumentMutation();
	const [, setDocumentEmail] = useState("");
	const rowId = row?.id;
	const rowSelectionId = getTrackerDocumentSelectionId(row);
	const isProcessingRow = Boolean(row?.__isProcessingRow);
	const isPendingRetryRow = Boolean(row?.__isPendingRetryRecord);
	const rowDisplayId = isPendingRetryRow ? "" : rowId;
	const isDocumentSelected = selectedDocumentIds.some(
		(selectedId) => String(selectedId) === String(rowSelectionId)
	);
	const isActiveRow = Boolean(activeRowId)
		? String(activeRowId) === String(rowSelectionId)
		: false;
	const documentId = getDocumentTrackerUploadedByDocumentId(row);

	const handleTitleClick = (event) => {
		event.stopPropagation();
		if (rowSelectionId) {
			setActiveRowId?.(rowSelectionId);
		}

		const selectedDocument =
			selectedDocuments.find(
				(item) => (item?.entityId ?? item?.id) === rowId
			) ??
			conditionDocuments.find(
				(item) => (item?.entityId ?? item?.id) === rowId
			) ??
			row;
		console.log("Selected Document:", matchingConditionDocuments);
		toggleDrawer(event, "document-tracker-detail", {
			selectedDocument: {
				...selectedDocument,
				statusLabel: loganStatusText,
				pipelineStatusText,
				loganStatusText,
				eFolderStatus: encompassDocStatusText,
			},
			loanId,
			isLoadingConditionsList:
				!isConditionStatusLoaded &&
				(Array.isArray(conditionTypeOptions)
					? conditionTypeOptions.length
					: 0) === 0,
			selectedConditionType:
				selectedConditionTypes?.[rowId] ?? row?.conditionType ?? "",
			selectedRowIds: selectedRow,
			matchingConditionDocuments,
			conditionTypeOptions,
			setConditionsOptions,
			onRefreshDocumentsList,
		});
	};

	const handleDocumentSelectionChange = (event) => {
		event.stopPropagation();
		onToggleDocumentSelection?.(row);
	};

	useEffect(() => {
		if (row?.__isPendingRetryRecord) {
			setDocumentEmail(row?.uploadedBy ?? "");
			return;
		}

		const uploadedByFallbackText = getDocumentTrackerUploadedByFallbackText(row);

		if (!shouldResolveDocumentTrackerUploadedBy(row) || !documentId) {
			setDocumentEmail(uploadedByFallbackText);
			return;
		}

		const cachedEmail = documentTrackerUploadedByCache.get(documentId);
		if (cachedEmail !== undefined) {
			setDocumentEmail(cachedEmail || uploadedByFallbackText);
			return;
		}

		let isMounted = true;

		const loadDocumentEmail = async () => {
			try {
				let requestPromise = documentTrackerUploadedByRequestCache.get(
					documentId
				);

				if (!requestPromise) {
					requestPromise = getUserEmailByDocument({
						documentId,
					})
						.unwrap()
						.then((response) => {
							const email =
								getDocumentTrackerUploadedByEmailFromResponse(
									response,
									documentId
								);

							documentTrackerUploadedByCache.set(documentId, email);
							return email;
						})
						.catch((error) => {
							console.warn(
								`[Document Email] lookup failed for ${documentId}:`,
								error
							);
							documentTrackerUploadedByCache.set(documentId, "");
							return "";
						})
						.finally(() => {
							documentTrackerUploadedByRequestCache.delete(documentId);
						});

					documentTrackerUploadedByRequestCache.set(documentId, requestPromise);
				}

				const resolvedEmail = await requestPromise;

				if (isMounted) {
					setDocumentEmail(resolvedEmail || uploadedByFallbackText);
				}
			} catch (error) {
				if (isMounted) {
					setDocumentEmail(uploadedByFallbackText);
				}
			}
		};

		loadDocumentEmail();

		return () => {
			isMounted = false;
		};
	}, [
		documentId,
		getUserEmailByDocument,
		row?.__isPendingRetryRecord,
		row?.uploadedBy,
		row?.createdBy?.entityId,
		row?.createdBy?.name,
	]);

	const hasAssignedTo = Array.isArray(row?.assignedTo)
		? row.assignedTo.length > 0
		: Boolean(row?.assignedTo);

	const {
		matchingConditionDocuments,
		matchingConditionCount,
		hasMatchingAttachment,
		isIndexed,
		indexedRecordTitle,
		isXpanseProcessingRecord,
	} = useMemo(
		() => getTrackerMatchingConditionInfo(row, conditionDocuments),
		[row, conditionDocuments]
	);
	const {
		loganStatusText,
		pipelineDotColors,
		pipelineStatusText,
		isYellowLoganStatus,
		encompassDocStatusText,
	} = useMemo(
		() =>
			getDocumentPipelineStatusPresentation({
				row,
				isIndexed,
				indexedRecordTitle,
				isXpanseProcessingRecord,
				matchingConditionCount,
				hasMatchingAttachment,
				hasAssignedTo,
				isPendingRetryRecord: Boolean(row?.__isPendingRetryRecord),
			}),
		[
			hasAssignedTo,
			hasMatchingAttachment,
			indexedRecordTitle,
			isIndexed,
			isXpanseProcessingRecord,
			matchingConditionCount,
			row,
		]
	);

	if (isProcessingRow) {
		return (
			<TableRow
				onClick={handleTitleClick}
				tabIndex={0}
				role="button"
				sx={{
					cursor: "pointer",
					"& > .MuiTableCell-root": {
						backgroundColor: isActiveRow ? "#EAF8EF" : "transparent",
					},
					"&:hover > .MuiTableCell-root": {
						backgroundColor: isActiveRow ? "#DDF4E4" : "#F3FAF5",
					},
					"&:focus-visible > .MuiTableCell-root": {
						backgroundColor: isActiveRow ? "#DDF4E4" : "#F3FAF5",
					},
				}}
			>
				<TableCell>
					
				</TableCell>
				<TableCell>
					<Stack direction="column" spacing={0.25} alignItems="flex-start">
						<Body2
							text={row?.fileName ?? row?.title ?? "-"}
							extraClass={styles.greenText}
							fontType="semibold"
						/>
						<Body3 text={rowId || "-"} extraClass={styles.pendingText} />
					</Stack>
				</TableCell>
				<TableCell>
					<Body2 text={row?.time ?? "-"} />
				</TableCell>
				<TableCell>
					<Body2 text={row?.uploadedBy ?? "-"} extraClass={styles.greenText} />
				</TableCell>
				<TableCell>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							px: 1.5,
							py: 0.75,
							borderRadius: "8px",
							maxWidth: 150,
							bgcolor: "#FFF4D6",
							border: "1px solid #F4C766",
						}}
					>
						<Typography
							component="span"
							sx={{
								color: "#C47B00",
								fontSize: "13px",
								fontWeight: 600,
								lineHeight: 1.2,
								whiteSpace: "normal",
								wordBreak: "break-word",
							}}
						>
							Processing Large File
						</Typography>
					</Box>
				</TableCell>
				<TableCell>
					<Body2 text="Pending" fontStyle="italic" extraClass={styles.pendingText} />
				</TableCell>
				<TableCell>
					<Stack direction="row" alignItems="center" spacing={1}>
						{[1, 2, 3, 4].map((item) => (
							<Box
								key={item}
								sx={{
									width: 18,
									height: 6,
									borderRadius: 10,
									bgcolor: item <= 1 ? "#F4BE3D" : "#E1E5EA",
								}}
							/>
						))}
						<Body2 text="Still Uploading" />
					</Stack>
				</TableCell>
				<TableCell>
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: 28,
							height: 28,
							borderRadius: "999px",
							bgcolor: "#EEF0FF",
						}}
					>
						<Typography
							component="span"
							sx={{
								color: "#6E77F7",
								fontSize: "14px",
								fontWeight: 600,
								lineHeight: 1,
							}}
						>
							0
						</Typography>
					</Box>
				</TableCell>
				<TableCell>
					<Stack direction="row" alignItems="center" gap={1}>
						<Typography
							component="span"
							sx={{
								color: "#A7A7A7",
								fontSize: "18px",
								lineHeight: 1,
							}}
						>
							-
						</Typography>
					</Stack>
				</TableCell>
			</TableRow>
		);
	}

	const formattedCreatedDate = (() => {
		if (!row?.createdDate) return "NA";

		const date = DateTime.fromISO(row?.createdDate, {
			zone: "America/New_York",
		});
		if (!date.isValid) return "NA";

		return `${date.toFormat("MMM d, yyyy, h:mm a")} ET`;
	})();

	const createdByDisplayText = getUploadedByDisplayText(row);

	const fileName = row?.entityName ?? row?.fileName ?? row?.title ?? "";
	const isRenderable = isViewableInBrowser(fileName);

	const handleViewClick = (event) => {
		event.stopPropagation();
		onViewDocument(row, isRenderable);
	};

	const handleDownloadClick = (event) => {
		event.stopPropagation();
		onDownloadDocument(row);
	};

	return (
		<TableRow
			onClick={handleTitleClick}
			tabIndex={0}
			role="button"
			selected={isActiveRow || isDocumentSelected}
			sx={{
				cursor: "pointer",
				"&.Mui-selected": {
					backgroundColor: "#EAF8EF",
				},
				"&.Mui-selected:hover": {
					backgroundColor: "#DDF4E4",
				},
				"& > .MuiTableCell-root": {
					backgroundColor:
						isActiveRow || isDocumentSelected ? "#EAF8EF" : "transparent",
				},
				"&:hover > .MuiTableCell-root": {
					backgroundColor:
						isActiveRow || isDocumentSelected ? "#DDF4E4" : "#F3FAF5",
				},
				"&:focus-visible > .MuiTableCell-root": {
					backgroundColor:
						isActiveRow || isDocumentSelected ? "#DDF4E4" : "#F3FAF5",
				},
			}}
		>
			<TableCell padding="checkbox">
				<Checkbox
					className={styles.checkboxContainer}
					checked={isDocumentSelected}
					disabled={isPendingRetryRow}
					onChange={handleDocumentSelectionChange}
					onClick={(event) => event.stopPropagation()}
					inputProps={{
						"aria-label": `select document ${row?.title ?? rowId ?? ""}`,
					}}
				/>
			</TableCell>
			<TableCell>
				<Stack direction="column" spacing={0.25} alignItems="flex-start">
					<Body2
						text={row.title}
						handleClick={handleTitleClick}
						extraClass={`${styles.greenText} ${styles.clickableText}`}
						fontType="semibold"
					/>
					{rowDisplayId ? (
						<Body3 text={rowDisplayId} extraClass={styles.pendingText} />
					) : null}
				</Stack>
			</TableCell>
			<TableCell>
				<Body2 text={formattedCreatedDate} />
			</TableCell>
			<TableCell>
				<Body2 text={createdByDisplayText} extraClass={styles.greenText} />
			</TableCell>
			<TableCell>
				<Box
					sx={{
						display: "inline-flex",
						alignItems: "center",
						px: 1.5,
						py: 0.75,
						borderRadius: "8px",
						maxWidth: 150,
						bgcolor: isYellowLoganStatus ? "#F9EDC2" : "#DCF6E5",
					}}
				>
					<Typography
						component="span"
						sx={{
							color: isYellowLoganStatus ? "#A66A1E" : "#2E7D32",
							fontSize: "15px",
							fontWeight: 400,
							lineHeight: 1.2,
							whiteSpace: "normal",
							wordBreak: "break-word",
						}}
					>
						{loganStatusText}
						{/* {row?.assignedTo
							? "Fully Received By Logan"
							: "Processing large File"} */}
					</Typography>
				</Box>
			</TableCell>

			<TableCell>
				<Body2
					text={encompassDocStatusText}
					fontStyle={
						[
							"Pending Index",
							"Pending Update To Loan File",
							"Pending",
						].includes(encompassDocStatusText)
							? "italic"
							: "normal"
					}
					extraClass={
						[
							"Pending Index",
							"Pending Update To Loan File",
							"Pending",
						].includes(encompassDocStatusText)
							? styles.pendingText
							: styles.greenText
					}
				/>
			</TableCell>
			<TableCell>
				<Stack direction="row" alignItems="center" spacing={1}>
					{[1, 2, 3, 4].map((item) => (
						<Box
							key={item}
							sx={{
								width: 18,
								height: 6,
								borderRadius: 10,
								bgcolor: pipelineDotColors[item - 1],
							}}
						/>
					))}

					{}
					<Body2 text={pipelineStatusText} />
				</Stack>
			</TableCell>
			<TableCell>
				<Box
					sx={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 28,
						height: 28,
						borderRadius: "999px",
						bgcolor: "#EEF0FF",
					}}
				>
					<Typography
						component="span"
						sx={{
							color: "#6E77F7",
							fontSize: "14px",
							fontWeight: 600,
							lineHeight: 1,
						}}
					>
						{matchingConditionCount}
					</Typography>
				</Box>
			</TableCell>
			<TableCell>
				<Stack direction="row" alignItems="center" gap={1}>
					<IconButton
						onClick={handleViewClick}
						disabled={isPendingRetryRow}
						size="small"
						title={
							isPendingRetryRow
								? "Not available until the retry completes"
								: "View document"
						}
						sx={{
							padding: "4px",
							"&:hover": { backgroundColor: "transparent" },
						}}
					>
						<VisibilityOutlined fontSize="small" />
					</IconButton>
					<IconButton
						onClick={handleDownloadClick}
						disabled={isPendingRetryRow}
						size="small"
						title={
							isPendingRetryRow
								? "Not available until the retry completes"
								: "Download document"
						}
						sx={{
							padding: "4px",
							"&:hover": { backgroundColor: "transparent" },
						}}
					>
						<DownloadOutlined fontSize="small" />
					</IconButton>
				</Stack>
			</TableCell>
		</TableRow>
	);
};

export default ConditionsDocumentTrackerRow;
